import type { SelectOption } from '@/components/filters/selectUtils';

export function parseGrainsDictionary(dict: GrainsDictionary): {
  grainValues: string[];
  lowestGrains: Partial<LowestGrains>;
  lowestGrainsOptions: Partial<LowestGrainsOptionsInfo>;
} {
  const lowestGrains: Partial<LowestGrains> = {};
  const lowestGrainsOptions: Partial<LowestGrainsOptionsInfo> = {};

  (Object.keys(dict) as GrainId[]).forEach((grainId) => {
    const entries = dict[grainId];
    if (!entries || entries.length === 0) return;

    const lowest = entries.find((o) => o.lowest_grain);
    if (!lowest) return;

    const valueKey = lowest.id;
    const labelKey = lowest.name ?? valueKey;

    lowestGrains[grainId] = valueKey;
    lowestGrainsOptions[grainId] = { valueKey, labelKey };
  });

  return {
    grainValues: Object.values(lowestGrains),
    lowestGrains,
    lowestGrainsOptions,
  };
}

export function getLowestGrainKey(
  grainName: GrainId,
  grains: Grains,
  grainsDictionary: GrainsDictionary,
): SelectOption {
  const fallback = { value: grainName, label: grainName };
  if (!grains?.[grainName]) return fallback;

  const entries = grainsDictionary?.[grainName];
  if (!Array.isArray(entries)) return fallback;

  const match = entries.find((o) => o.lowest_grain);
  if (!match?.id) return fallback;

  return {
    value: match.id,
    label: match.name ?? match.id,
  };
}

export function parseGrainOptions(
  data: GrainValueRow[],
  valueKey: string,
  labelKey?: string,
  prependId = false,
): SelectOption[] {
  const options: SelectOption[] = [];
  const seen = new Set<string>();

  for (const r of data) {
    const value = r[valueKey];
    if (!value) continue;

    const effectiveLabelKey = labelKey || valueKey;
    let label = r[effectiveLabelKey] ?? value;

    if (prependId && label !== value) {
      label = `${value} - ${label}`;
    }

    if (!seen.has(value)) {
      seen.add(value);
      options.push({ value, label });
    }
  }

  return options.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}
